import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Output } from '@angular/core';
import { filter } from 'rxjs';
import { BreadcrumbService } from 'src/app/services/breadcrumb.service';
import { environment } from 'src/environments/environment';
import { ConfigService } from 'src/app/services/config.service';
import { AclService, EAccessName, EAccessType } from 'src/app/libs/services/acl.service';

export type MenuItem = {
  link: string,
  order: number,
  items?: Record<string, MenuItem>,
};


@Component({
    selector: 'app-main-menu-panel',
    templateUrl: './main-menu-panel.component.html',
    styleUrls: ['./main-menu-panel.component.sass'],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('0.5s ease-in-out', style({ opacity: 1 }))
            ]),
        ]),
    ],
    standalone: false
})
export class MainMenuPanelComponent {
  menu: Record<string, MenuItem> = {}

  readonly DIRModule = 'dir';
  readonly HRModule = 'hr';
  readonly AMSModule = 'ams';
  readonly ProductsModule = 'products';
  readonly WMSModule = 'wms';
  readonly PMSModule = 'pms';
  readonly OMSModule = 'oms';
  readonly APSModule = 'aps';
  readonly MESModule = 'mes';
  readonly FMSModule = 'fms';
  readonly TMSModule = 'tms';
  readonly OperativeZone = 'operative';
  readonly AnalysisZone = 'analysis';
  readonly ConfigurationZone = 'configuration';
  readonly PurchasesZone = 'purchases';
  readonly SalesZone = 'sales';
  readonly LogisticsZone = 'logistics';
  readonly ProductionZone = 'production';
  readonly QualityZone = 'quality';
  readonly MaintenanceZone = 'maintenance';

  sortByOrder = (a: {value: {order: number}}, b: {value: {order: number}}) => a.value.order - b.value.order;

  breadcrumb$ = this.breadcrumb.menuItems$.pipe(
    filter(items => {
      return items.length > 0;
    }),
  );

  @Output() onNavigate: EventEmitter<string>;

  production = environment.production;
  visible = false;

  readonly version = environment.version;
  readonly mail = 'suport@glamsw.com';
  readonly phone = '+34 972 183 310';

  constructor(private breadcrumb: BreadcrumbService, private configMenuService: ConfigService, private acl: AclService) {
    this.onNavigate = new EventEmitter<string>();
    this.menu = this.buildMenuModules();
  }

  navigate(link: string): void {
    this.onNavigate.emit(link);
  }

  private buildMenuModules() {
    const builtMenu: Record<string, MenuItem> = {};

    this.buildDirModule(builtMenu);
    this.buildHRModule(builtMenu);
    this.buildAMSModule(builtMenu);
    this.buildProductsModule(builtMenu);
    this.buildPMSModule(builtMenu);
    this.buildAPSModule(builtMenu);
    this.buildOMSModule(builtMenu);
    this.buildWMSModule(builtMenu);
    this.buildMESModule(builtMenu);
    this.buildTMSModule(builtMenu);
    this.buildFMSModule(builtMenu);

    return builtMenu
  }

  private buildDirModule(builtMenu: {[key: string]: {link: string, order: number, items?: {[key: string]: {link: string, order: number}}}}) {
    if (this.configMenuService.isApplicationVisible(this.DIRModule)
      && this.acl.isAllowed(EAccessName.Organizations)) {
      builtMenu[this.DIRModule] = { link: `/${this.DIRModule}`, order: 1, items: {} }

      if (this.configMenuService.isZoneVisible(this.DIRModule, this.OperativeZone)) {
        builtMenu[this.DIRModule].items![this.OperativeZone] = {
          link: `/${this.DIRModule}/${this.OperativeZone}`,
          order: 1
        }
      }

      if (this.configMenuService.isZoneVisible(this.DIRModule, this.ConfigurationZone)) {
        builtMenu[this.DIRModule].items![this.ConfigurationZone] = { link: `/${this.DIRModule}/${this.ConfigurationZone}`, order: 2}
      }
    }
  }

  private buildHRModule(builtMenu: Record<string, MenuItem>) {
    if (this.configMenuService.isApplicationVisible(this.HRModule)
      && (this.acl.isAllowed(EAccessName.Hhrr)
        || this.acl.isAllowed(EAccessName.Workers)
        || this.acl.isAllowed(EAccessName.Attendance)
        || this.acl.isAllowed(EAccessName.HeadOfDepartment, EAccessType.Activable)
      )
    ) {
      builtMenu[this.HRModule] = { link: `/${this.HRModule}`, order: 2, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(this.HRModule, this.OperativeZone)
        && (this.acl.isAllowed(EAccessName.Workers) || this.acl.isAllowed(EAccessName.Attendance) || this.acl.isAllowed(EAccessName.HeadOfDepartment, EAccessType.Activable))) {
        builtMenu[this.HRModule].items![this.OperativeZone] = { link: `/${this.HRModule}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(this.HRModule, this.AnalysisZone)
        && (this.acl.isAllowed(EAccessName.WorkersCostAnalysis))) {
        builtMenu[this.HRModule].items![this.AnalysisZone] = { link: `/${this.HRModule}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(this.HRModule, this.ConfigurationZone)
        && this.acl.isAllowed(EAccessName.Hhrr)) {
        builtMenu[this.HRModule].items![this.ConfigurationZone] = { link: `/${this.HRModule}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildAMSModule(builtMenu: Record<string, MenuItem>) {
    if (this.configMenuService.isApplicationVisible(this.AMSModule)
      && (this.acl.isAllowed(EAccessName.AssetsOperative)
        || this.acl.isAllowed(EAccessName.AssetsConfiguration))
    ) {
      const module = this.AMSModule;
      builtMenu[module] = { link: `/${module}`, order: 3, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(module, this.OperativeZone)
        && (this.acl.isAllowed(EAccessName.AssetsOperative))) {
        builtMenu[module].items![this.OperativeZone] = { link: `/${module}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(module, this.AnalysisZone)) {
        builtMenu[module].items![this.AnalysisZone] = { link: `/${module}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(module, this.ConfigurationZone)
        && (this.acl.isAllowed(EAccessName.AssetsConfiguration))) {
        builtMenu[module].items![this.ConfigurationZone] = { link: `/${module}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildProductsModule(builtMenu: Record<string, MenuItem>) {
    if (this.configMenuService.isApplicationVisible(this.ProductsModule)
      && (this.acl.isAllowed(EAccessName.ProductsOperative)
        || this.acl.isAllowed(EAccessName.CostPriceOperative)
        || this.acl.isAllowed(EAccessName.SalePriceOperative)
        || this.acl.isAllowed(EAccessName.ProcessSheetOperative))
    ) {
      const module = this.ProductsModule;
      builtMenu[module] = { link: `/${module}`, order: 4, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(module, this.OperativeZone)) {
        builtMenu[module].items![this.OperativeZone] = { link: `/${module}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(module, this.AnalysisZone)) {
        builtMenu[module].items![this.AnalysisZone] = { link: `/${module}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(module, this.ConfigurationZone)) {
        builtMenu[module].items![this.ConfigurationZone] = { link: `/${module}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildWMSModule(builtMenu: Record<string, MenuItem>) {
    if (this.configMenuService.isApplicationVisible(this.WMSModule)
      && (this.acl.isAllowed(EAccessName.WmsOperative)
        || this.acl.isAllowed(EAccessName.WmsConfiguration))
    ) {
      const module = this.WMSModule;
      builtMenu[module] = { link: `/${module}`, order: 9, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(module, this.OperativeZone)
        && (this.acl.isAllowed(EAccessName.WmsOperative))) {
        builtMenu[module].items![this.OperativeZone] = { link: `/${module}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(module, this.AnalysisZone)) {
        builtMenu[module].items![this.AnalysisZone] = { link: `/${module}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(module, this.ConfigurationZone)
        && (this.acl.isAllowed(EAccessName.WmsConfiguration))) {
        builtMenu[module].items![this.ConfigurationZone] = { link: `/${module}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildPMSModule(builtMenu: Record<string, MenuItem>) {
    if (this.configMenuService.isApplicationVisible(this.PMSModule)
      && (this.acl.isAllowed(EAccessName.ProjectsConfiguration)
        || this.acl.isAllowed(EAccessName.ProjectsOperative))
    ) {
      const module = this.PMSModule;
      builtMenu[module] = { link: `/${module}`, order: 5, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(module, this.OperativeZone)
        && (this.acl.isAllowed(EAccessName.ProjectsOperative))) {
        builtMenu[module].items![this.OperativeZone] = { link: `/${module}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(module, this.AnalysisZone)) {
        builtMenu[module].items![this.AnalysisZone] = { link: `/${module}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(module, this.ConfigurationZone)
        && (this.acl.isAllowed(EAccessName.ProjectsConfiguration))) {
        builtMenu[module].items![this.ConfigurationZone] = { link: `/${module}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildOMSModule(builtMenu: Record<string, MenuItem>) {
    const isAllowedPurchases =
      this.acl.isAllowed(EAccessName.SupplyRequisitionOrdersOperative) ||
      this.acl.isAllowed(EAccessName.PurchaseOrdersOperative);
    const isAllowedSales = this.acl.isAllowed(EAccessName.SaleOrdersOperative);
    const isAllowedLogistics =
      this.acl.isAllowed(EAccessName.DispatchOrdersOperative) ||
      this.acl.isAllowed(EAccessName.TransferOrders) ||
      this.acl.isAllowed(EAccessName.ReservationOrdersOperative) ||
      this.acl.isAllowed(EAccessName.WmsOperative);
    const isAllowedProduction = this.acl.isAllowed(EAccessName.ProductionOrdersOperative);
    const isAllowedQuality = this.acl.isAllowed(EAccessName.NonConformitiesOperative) || this.acl.isAllowed(EAccessName.QualityControlOperative);
    const isAllowedMaintenance = this.acl.isAllowed(EAccessName.SupportRequest) || this.acl.isAllowed(EAccessName.MaintenanceOrdersOperative)
    if (this.configMenuService.isApplicationVisible(this.OMSModule)
      && (isAllowedPurchases || isAllowedSales || isAllowedLogistics || isAllowedProduction || isAllowedQuality || isAllowedMaintenance)
    ) {
      const module = this.OMSModule;
      builtMenu[module] = { link: `/${module}`, order: 7, items: {} }
      if (this.configMenuService.isZoneVisible(module, this.PurchasesZone) && isAllowedPurchases) {
        builtMenu[module].items![this.PurchasesZone] = { link: `/${module}/${this.PurchasesZone}`, order: 1 }
      }
      if (this.configMenuService.isZoneVisible(module, this.SalesZone) && isAllowedSales) {
        builtMenu[module].items![this.SalesZone] = { link: `/${module}/${this.SalesZone}`, order: 2 }
      }
      if (this.configMenuService.isZoneVisible(module, this.LogisticsZone) && isAllowedLogistics) {
        builtMenu[module].items![this.LogisticsZone] = { link: `/${module}/${this.LogisticsZone}`, order: 3 }
      }
      if (this.configMenuService.isZoneVisible(module, this.ProductionZone) && isAllowedProduction) {
        builtMenu[module].items![this.ProductionZone] = { link: `/${module}/${this.ProductionZone}`, order: 4 }
      }
      if (this.configMenuService.isZoneVisible(module, this.QualityZone) && isAllowedQuality) {
        builtMenu[module].items![this.QualityZone] = { link: `/${module}/${this.QualityZone}`, order: 5 }
      }
      if (this.configMenuService.isZoneVisible(module, this.MaintenanceZone) && isAllowedMaintenance) {
        builtMenu[module].items![this.MaintenanceZone] = { link: `/${module}/${this.MaintenanceZone}`, order: 6 }
      }
    }
  }

  private buildAPSModule(builtMenu: Record<string, MenuItem>) {
    const isOperativeAllowed =
      this.acl.isAllowed(EAccessName.PlanificationOperative) ||
      this.acl.isAllowed(EAccessName.ProcessSheetOperative) ||
      this.acl.isAllowed(EAccessName.MaintenancePlanOperative);

    if (this.configMenuService.isApplicationVisible(this.APSModule) && (isOperativeAllowed)) {
      const module = this.APSModule;
      builtMenu[module] = { link: `/${module}`, order: 6, items: {} };

      // Operative zone
      if (this.configMenuService.isZoneVisible(module, this.OperativeZone) && isOperativeAllowed) {
        builtMenu[module].items![this.OperativeZone] = {
          link: `/${module}/${this.OperativeZone}`,
          order: 1
        };
      }

      // Configuration zone
      if (this.configMenuService.isZoneVisible(module, this.ConfigurationZone) && isOperativeAllowed) {
        builtMenu[module].items![this.ConfigurationZone] = {
          link: `/${module}/${this.ConfigurationZone}`,
          order: 2
        };
      }
    }
  }


  private buildTMSModule(builtMenu: Record<string, MenuItem>) {
    // if (this.configMenuService.isApplicationVisible(this.TMSModule)
    if (this.configMenuService.isApplicationVisible(this.TMSModule)
      && this.acl.isAllowed(EAccessName.DispatchOrdersOperative)
    ) {
      builtMenu[this.TMSModule] = { link: `/${this.TMSModule}`, order: 11, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(this.TMSModule, this.OperativeZone)) {
        builtMenu[this.TMSModule].items![this.OperativeZone] = { link: `/${this.TMSModule}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(this.TMSModule, this.AnalysisZone)) {
        builtMenu[this.TMSModule].items![this.AnalysisZone] = { link: `/${this.TMSModule}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(this.TMSModule, this.ConfigurationZone)) {
        builtMenu[this.TMSModule].items![this.ConfigurationZone] = { link: `/${this.TMSModule}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildFMSModule(builtMenu: Record<string, MenuItem>) {
    // if (this.configMenuService.isApplicationVisible(this.TMSModule)
    if (this.configMenuService.isApplicationVisible(this.FMSModule)
      && (this.acl.isAllowed(EAccessName.SupplierInvoices) || this.acl.isAllowed(EAccessName.SupplierInvoicesRegister) || this.acl.isAllowed(EAccessName.FinanceConfiguration))
    ) {
      builtMenu[this.FMSModule] = { link: `/${this.FMSModule}`, order: 12, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(this.FMSModule, this.OperativeZone)) {
        builtMenu[this.FMSModule].items![this.OperativeZone] = { link: `/${this.FMSModule}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(this.FMSModule, this.AnalysisZone)) {
        builtMenu[this.FMSModule].items![this.AnalysisZone] = { link: `/${this.FMSModule}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(this.FMSModule, this.ConfigurationZone)) {
        builtMenu[this.FMSModule].items![this.ConfigurationZone] = { link: `/${this.FMSModule}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildMESModule(builtMenu: Record<string, MenuItem>) {
    if (this.configMenuService.isApplicationVisible(this.MESModule) && (this.acl.isAllowed(EAccessName.ProductionControlOperative) || this.acl.isAllowed(EAccessName.ProductionTicketsOperative))) {
      builtMenu[this.MESModule] = { link: `/${this.MESModule}`, order: 10, items: {} }
      // Operative zone
      if (this.configMenuService.isZoneVisible(this.MESModule, this.OperativeZone) && this.acl.isAllowed(EAccessName.ProductionTicketsOperative)) {
        builtMenu[this.MESModule].items![this.OperativeZone] = { link: `/${this.MESModule}/${this.OperativeZone}`, order: 1 }
      }
      // Analysis zone
      if (this.configMenuService.isZoneVisible(this.MESModule, this.AnalysisZone) && this.acl.isAllowed(EAccessName.ProductionTicketsOperative)) {
        builtMenu[this.MESModule].items![this.AnalysisZone] = { link: `/${this.MESModule}/${this.AnalysisZone}`, order: 2 }
      }
      // Configuration zone
      if (this.configMenuService.isZoneVisible(this.MESModule, this.ConfigurationZone) && this.acl.isAllowed(EAccessName.ProductionTicketsOperative)) {
        builtMenu[this.MESModule].items![this.ConfigurationZone] = { link: `/${this.MESModule}/${this.ConfigurationZone}`, order: 3 }
      }
    }
  }

  private buildModule(module: string, moduleOrder: number, builtMenu: Record<string, MenuItem>) {
    if (this.configMenuService.isApplicationVisible(module)){
      builtMenu[module] = { link: `/${module}`, order: moduleOrder, items: {} }
      this.buildGenericZones(module, builtMenu);
    }
  }

  private buildGenericZones(module: string, builtMenu: Record<string, MenuItem>) {
    // Operative zone
    if (this.configMenuService.isZoneVisible(module, this.OperativeZone)) {
      builtMenu[module].items![this.OperativeZone] = { link: `/${module}/${this.OperativeZone}`, order: 1 }
    }
    // Analysis zone
    if (this.configMenuService.isZoneVisible(module, this.AnalysisZone)) {
      builtMenu[module].items![this.AnalysisZone] = { link: `/${module}/${this.AnalysisZone}`, order: 2 }
    }
    // Configuration zone
    if (this.configMenuService.isZoneVisible(module, this.ConfigurationZone)) {
      builtMenu[module].items![this.ConfigurationZone] = { link: `/${module}/${this.ConfigurationZone}`, order: 3 }
    }
  }
}
